import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["active", "actions", "onHandleMouseDown", "onDropdownOpenChange", "responsiveSize", "connectDragSource", "isSelectedItem"];
import PropTypes from 'prop-types';
import { memoize } from 'underscore';
import I18n from 'I18n';
import { ColumnIcon, EditIcon, DuplicateIcon, DeleteIcon, CustomHublIcon, HideIcon } from 'foundations-assets';
import { TrellisHorizontalEdgeToolbar as HorizontalEdgeToolbar } from 'layout-dnd-components/DndToolbar/HorizontalEdgeToolbar';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { updateSelectedItem
// @ts-expect-error not typed
} from 'ContentEditorUI/redux/actions/selectionActions';
import { cloneColumnFromLayoutSection, requestCloneFromLayoutSection, deleteColumnFromLayoutSection
// @ts-expect-error not typed
} from 'ContentEditorUI/redux/actions/moduleActions';
import { COLUMN } from 'ContentEditorUI/constants/cloneTypes';
// @ts-expect-error not typed
import { logLayoutSectionEvent } from 'InpageEditorUI/eventsAsActions/layoutTransitionEventsAsActions';
// @ts-expect-error not typed
import { LAYOUT_SECTION_TRACKING } from 'ContentEditorUI/lib/layoutSection/constants';
// @ts-expect-error not typed
import { startGeneratedHublRequest } from 'ContentEditorUI/redux/actions/hublActions';
import { hashFunctionForObject } from './helpers';
import { jsx as _jsx } from "react/jsx-runtime";
export const buildStyleColumnAction = (columnName, layoutSectionId, dispatch, bus, {
  onlyInDropdown
} = {}) => ({
  id: 'column-styles',
  icon: EditIcon,
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.editStyles"
  }),
  tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.editStyles"
  }),
  onlyInDropdown,
  callback: () => {
    dispatch(updateSelectedItem(columnName, {
      containerId: layoutSectionId
    }));
    bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.EDIT_LAYOUT_STYLES, {
      type: 'COLUMN'
    }));
  }
});
export const buildDuplicateColumnAction = (columnName, layoutSectionId, dispatch, bus, {
  onlyInDropdown,
  canBeCloned
} = {}) => {
  const menuLabel = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.clone"
  });
  let menuTooltip = undefined;
  let tooltipLabel = menuLabel;
  if (!canBeCloned) {
    menuTooltip = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.column.cantCloneDetail"
    });
    tooltipLabel = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.column.cantCloneDetail"
    });
  }
  return {
    id: 'column-duplicate',
    icon: DuplicateIcon,
    menuLabel,
    menuTooltip,
    tooltipLabel,
    onlyInDropdown,
    disabled: !canBeCloned,
    callback: () => {
      bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.CLONE_COLUMN));
      dispatch(requestCloneFromLayoutSection(columnName, layoutSectionId, COLUMN, cloneColumnFromLayoutSection(columnName, layoutSectionId)));
    }
  };
};
export const buildDeleteColumnAction = (columnName, layoutSectionId, dispatch, bus, {
  onlyInDropdown
} = {}) => ({
  id: 'column-delete',
  icon: DeleteIcon,
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.delete"
  }),
  tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.delete"
  }),
  onlyInDropdown,
  callback: () => {
    bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.DELETE_COLUMN));
    dispatch(deleteColumnFromLayoutSection(columnName, layoutSectionId));
  }
});
const buildCopyAsHublAction = (columnName, layoutSectionId, dispatch) => ({
  id: 'copy-as-hubl',
  icon: CustomHublIcon,
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.copy"
  }),
  menuTooltip: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.copy"
  }),
  onlyInDropdown: true,
  callback: () => {
    dispatch(startGeneratedHublRequest(columnName, layoutSectionId));
  }
});
const buildHideOnMobileAction = (bus, toggleNodeVisibilityOnThisBreakpoint) => ({
  id: 'column-hide',
  icon: HideIcon,
  menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.hide"
  }),
  tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.column.hide"
  }),
  callback: e => {
    if (e) {
      bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.HIDE_COLUMN));
      toggleNodeVisibilityOnThisBreakpoint(false, e);
    }
  }
});
export const buildActionsForColumn = memoize(({
  columnName,
  layoutSectionId,
  isInDefaultBreakpointMode,
  canBeCloned,
  dispatch,
  bus,
  isInDeveloperMode,
  toggleNodeVisibilityOnThisBreakpoint
}) => {
  const actions = [buildStyleColumnAction(columnName, layoutSectionId, dispatch, bus, {
    onlyInDropdown: false
  })];
  if (isInDefaultBreakpointMode) {
    actions.push(buildDuplicateColumnAction(columnName, layoutSectionId, dispatch, bus, {
      onlyInDropdown: false,
      canBeCloned
    }), buildDeleteColumnAction(columnName, layoutSectionId, dispatch, bus, {
      onlyInDropdown: false
    }));
    if (isInDeveloperMode) {
      actions.push(buildCopyAsHublAction(columnName, layoutSectionId, dispatch));
    }
  } else {
    actions.push(buildHideOnMobileAction(bus, toggleNodeVisibilityOnThisBreakpoint));
  }
  return actions;
}, hashFunctionForObject);
export const BaseResponsiveColumnToolbar = _ref => {
  let {
      active,
      actions,
      onHandleMouseDown,
      onDropdownOpenChange,
      responsiveSize,
      connectDragSource,
      isSelectedItem
    } = _ref,
    toolbarProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const buttonActions = [...actions];
  const styleActionIndex = buttonActions.findIndex(action => action.id === 'column-styles');

  // Assume style action button's callback should be used when clicking on the "icon" of this toolbar
  const iconClickHandler = styleActionIndex === -1 || buttonActions[styleActionIndex].disabled ? undefined : buttonActions[styleActionIndex].callback;
  if (isSelectedItem && styleActionIndex !== -1) {
    buttonActions.splice(styleActionIndex, 1);
  }
  return /*#__PURE__*/_jsx(HorizontalEdgeToolbar, Object.assign({
    "data-test-id": "hs-ls-responsive-column-toolbar",
    position: "absolute",
    zIndex: 5,
    active: active,
    icon: ColumnIcon,
    actions: buttonActions,
    responsiveSize: responsiveSize,
    handleTooltip: I18n.text('inpage.actionToolbar.column.label'),
    onHandleMouseDown: onHandleMouseDown,
    onDropdownOpenChange: onDropdownOpenChange,
    onHandleClick: iconClickHandler,
    handleButtonRef: handleButtonElement => {
      if (handleButtonElement && connectDragSource) {
        connectDragSource(handleButtonElement);
      }
    },
    handleTooltipPlacement: "top right",
    handleButtonStyles: {
      cursor: 'move',
      borderWidth: '100'
    },
    draggable: true,
    variant: "primary"
  }, toolbarProps));
};
BaseResponsiveColumnToolbar.propTypes = {
  active: PropTypes.bool.isRequired,
  style: PropTypes.object,
  actions: PropTypes.array.isRequired,
  onHandleMouseDown: PropTypes.func,
  onDropdownOpenChange: PropTypes.func,
  responsiveSize: PropTypes.string,
  connectDragSource: PropTypes.func,
  isSelectedItem: PropTypes.bool
};